import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, TrendingUp, CheckCircle, CheckCircle2 } from 'lucide-react';

const AdvantageCard = ({
  icon: Icon,
  title,
  description,
  delay,
}: {
  icon: any;
  title: string;
  description: string;
  delay: string;
}) => {
  return (
    <motion.div
      className="bg-gradient-to-br from-stone-50 to-white rounded-xl p-8 border border-stone-200 hover:border-serchon-gold/50 transition-all duration-300"
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: parseFloat(delay) }}
      whileHover={{ scale: 1.02, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-serchon-gold rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="font-serif text-xl font-bold text-stone-900 mb-2">{title}</h3>
          <p className="text-stone-600 leading-relaxed">{description}</p>
        </div>
      </div>
    </motion.div>
  );
};

const AdvantageSection: React.FC = () => {
  const advantages = [
    {
      icon: Users,
      title: '专业团队',
      description:
        '拥有经验丰富的技术团队，深耕行业多年，能够深入理解客户需求，提供专业的技术解决方案。',
      delay: '0',
    },
    {
      icon: TrendingUp,
      title: '持续创新',
      description:
        '紧跟技术发展趋势，不断创新技术方案，确保客户始终享有最先进的技术服务。',
      delay: '0.1',
    },
    {
      icon: Award,
      title: '质量保障',
      description:
        '严格的质量管理体系，从需求分析到交付上线，全程把控项目质量，确保交付成果。',
      delay: '0.2',
    },
    {
      icon: CheckCircle2,
      title: '优质服务',
      description:
        '以客户为中心的服务理念，提供全方位的技术支持，确保项目顺利完成并持续优化。',
      delay: '0.3',
    },
  ];

  return (
    <section id="advantage" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block mb-4 text-xs font-bold tracking-widest text-serchon-gold uppercase">
              核心优势
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mb-6 text-stone-900">为什么选择我们</h2>
            <p className="text-lg text-stone-600 mb-8 leading-relaxed">
              溯玄科技致力于为企业提供卓越的技术服务，以专业、创新、品质为核心竞争力，成为值得信赖的技术合作伙伴。
            </p>

            <div className="space-y-4">
              {advantages.slice(0, 2).map((advantage, index) => (
                <AdvantageCard
                  key={index}
                  icon={advantage.icon}
                  title={advantage.title}
                  description={advantage.description}
                  delay={advantage.delay}
                />
              ))}
            </div>
          </motion.div>

          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {advantages.slice(2).map((advantage, index) => (
              <AdvantageCard
                key={index}
                icon={advantage.icon}
                title={advantage.title}
                description={advantage.description}
                delay={advantage.delay}
              />
            ))}

            <motion.div
              className="bg-stone-900 text-white rounded-xl p-8 mt-8"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-serchon-gold rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-serif text-xl font-bold mb-2">值得信赖</h3>
                  <p className="text-stone-400 leading-relaxed">
                    自成立以来，我们始终坚持客户至上的原则，以专业的技术能力和优质的服务赢得了客户的一致好评。
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AdvantageSection;
